import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, Mail, Lock, ArrowLeft, Sun, Moon } from 'lucide-react';

const LOGO_LIGHT = 'https://customer-assets.emergentagent.com/job_request-flow-12/artifacts/nazg9foa_United%20Group%20PNG.png';
const LOGO_DARK = 'https://customer-assets.emergentagent.com/job_request-flow-12/artifacts/ky9isxfd_United%20Group%20White%20logo%20PNG%20%281%29.png';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const _from = location.state?.from;
  const redirectTo = _from ? `${_from.pathname}${_from.search || ''}` : '/dashboard';
  const { isAuthenticated, loading, loginWithPassword } = useAuth();
  const { requestDesktopPermission } = useNotifications();
  const { theme, toggleTheme } = useTheme();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [keepSignedIn, setKeepSignedIn] = useState(false);
  
  // Use theme-aware logo
  const isDark = theme === 'dark';
  const currentLogo = isDark ? LOGO_DARK : LOGO_LIGHT;
  
  // Forgot password states
  const [forgotDialog, setForgotDialog] = useState(false);
  const [resetStep, setResetStep] = useState(1); // 1 = email, 2 = code + new password
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate(redirectTo);
    }
  }, [loading, isAuthenticated, navigate, redirectTo]);

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    
    if (!email.trim() || !password.trim()) {
      toast.error('Please enter email and password');
      return;
    }

    // Validate domain
    if (!email.toLowerCase().endsWith('@united-group.in')) {
      toast.error('Only @united-group.in email addresses are allowed');
      return;
    }

    setLoginLoading(true);
    try {
      // Request notification permission non-blocking (don't await - Edge can hang on this)
      try { requestDesktopPermission(); } catch (_) {}
      await loginWithPassword(email, password, keepSignedIn);
      navigate(redirectTo);
    } catch (error) {
      toast.error(error.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!forgotEmail.trim()) {
      toast.error('Please enter your email');
      return;
    }

    if (!forgotEmail.toLowerCase().endsWith('@united-group.in')) {
      toast.error('Only @united-group.in email addresses are allowed');
      return;
    }

    setResetLoading(true);
    try {
      const res = await fetch(`${API}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail })
      });
      // Capture status BEFORE attempting body read — some envs (preview proxies,
      // Emergent automation hooks) may have already consumed the body stream.
      const status = res.status;
      const ok = res.ok;
      let data = {};
      try {
        const txt = await res.clone().text();
        data = txt ? JSON.parse(txt) : {};
      } catch (_e) {
        // Body already consumed by an upstream interceptor — fall back to status-only handling
        data = {};
      }
      if (ok) {
        if (data.email_sent) {
          toast.success('Password reset code has been sent to your email');
        } else {
          toast.success('Reset code generated. Please check your email.');
        }
        setResetStep(2);
      } else if (status === 404) {
        // First-time user — guide them to sign up
        toast.error(data.detail || 'No account found for this email. Please sign up first.', {
          duration: 8000,
        });
        // Auto-redirect to signup after a short delay so the user reads the message
        setTimeout(() => {
          setForgotDialog(false);
          navigate(`/signup?email=${encodeURIComponent(forgotEmail)}`);
        }, 1600);
      } else {
        toast.error(data.detail || 'Failed to generate reset code');
      }
    } catch (error) {
      toast.error('Failed to process request');
    } finally {
      setResetLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetCode.trim()) {
      toast.error('Please enter the reset code');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setResetLoading(true);
    try {
      const res = await fetch(`${API}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: forgotEmail,
          reset_code: resetCode,
          new_password: newPassword
        })
      });

      const data = await res.json();
      if (res.ok) {
        toast.success('Password reset successfully! You can now login.');
        setForgotDialog(false);
        setResetStep(1);
        setForgotEmail('');
        setResetCode('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        toast.error(data.detail || 'Failed to reset password');
      }
    } catch (error) {
      toast.error('Failed to reset password');
    } finally {
      setResetLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen login-bg flex items-center justify-center p-4 relative">
      {/* Theme toggle button */}
      <Button
        variant="ghost"
        size="icon"
        onClick={toggleTheme}
        className="absolute top-4 right-4 rounded-full bg-background/80 backdrop-blur-sm shadow-md"
        data-testid="login-theme-toggle"
      >
        {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
      </Button>

      <Card className="w-full max-w-md shadow-2xl bg-white/90 dark:bg-zinc-900/95 backdrop-blur-md border border-white/30 dark:border-white/10" data-testid="login-card">
        <CardHeader className="text-center space-y-4">
          <div className="flex justify-center p-2 rounded-lg dark:bg-zinc-800">
            <img src={currentLogo} alt="United Group" className="h-12 w-auto max-w-[180px] object-contain" />
          </div>
          <div>
            <CardTitle className="text-2xl font-bold tracking-tight">
              Help Desk
            </CardTitle>
            <CardDescription className="mt-2">
              Sign in with your company credentials
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <form onSubmit={handlePasswordLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="your.email@united-group.in"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  data-testid="email-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10"
                  data-testid="password-input"
                />
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="keep-signed-in"
                  checked={keepSignedIn}
                  onCheckedChange={(v) => setKeepSignedIn(!!v)}
                  data-testid="keep-signed-in-checkbox"
                />
                <Label
                  htmlFor="keep-signed-in"
                  className="text-sm font-normal cursor-pointer select-none"
                >
                  Keep me signed in for 30 days
                </Label>
              </div>
              <Button
                type="button"
                variant="link"
                className="p-0 h-auto text-sm"
                onClick={() => {
                  setForgotDialog(true);
                  setResetStep(1);
                }}
                data-testid="forgot-password-link"
              >
                Forgot Password?
              </Button>
            </div>
            
            <Button
              type="submit"
              className="w-full h-12 text-base font-medium btn-press"
              disabled={loginLoading}
              data-testid="login-btn"
            >
              {loginLoading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                'Sign In'
              )}
            </Button>
          </form>
          
          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">Or</span>
            </div>
          </div>
          
          <Button
            type="button"
            variant="outline"
            className="w-full h-12 text-base font-medium"
            onClick={() => navigate('/signup')}
            data-testid="signup-btn"
          >
            Sign Up
          </Button>
          
          <p className="text-xs text-center text-muted-foreground mt-4">
            Only @united-group.in email addresses are allowed
          </p>
        </CardContent>
      </Card>

      {/* Forgot Password Dialog */}
      <Dialog open={forgotDialog} onOpenChange={setForgotDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {resetStep === 1 ? 'Forgot Password' : 'Reset Password'}
            </DialogTitle>
            <DialogDescription>
              {resetStep === 1 
                ? 'Enter your email to receive a reset code'
                : 'Enter the reset code and your new password'
              }
            </DialogDescription>
          </DialogHeader>
          
          {resetStep === 1 ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  placeholder="your.email@united-group.in"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  data-testid="forgot-email-input"
                />
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setResetStep(1)}
                className="gap-1 p-0 h-auto"
              >
                <ArrowLeft className="w-4 h-4" /> Back
              </Button>
              
              <div className="space-y-2">
                <Label>Reset Code</Label>
                <Input
                  placeholder="Enter 6-digit code"
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value)}
                  maxLength={6}
                  data-testid="reset-code-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>New Password</Label>
                <Input
                  type="password"
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  data-testid="new-password-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Confirm Password</Label>
                <Input
                  type="password"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  data-testid="confirm-password-input"
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setForgotDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={resetStep === 1 ? handleForgotPassword : handleResetPassword}
              disabled={resetLoading}
              data-testid="reset-submit-btn"
            >
              {resetLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {resetStep === 1 ? 'Get Reset Code' : 'Reset Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
